/* ============================================================
   Laley PDP Clean v4 — siblings
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Registrerer metoder på LaleyPDP.prototype via PDP.registerModule.
   Krever pdp-clean-v4-core.js lastet først (defer-rekkefølge i product-clean-v3-1.liquid).
   ============================================================ */
(() => {
  const PDP = window.LaleyPDPCleanV4;
  if (!PDP || typeof PDP.registerModule !== 'function') {
    console.error('[PDP] Kjerne mangler — hopper over modul: siblings');
    return;
  }

  const SIBLING_COLOR_TOKENS = [
    { token: 'grey-melange', label: 'Gråmelert', hex: '#9f9a93' },
    { token: 'soft-pink', label: 'Soft pink', hex: '#f1d1d8' },
    { token: 'deep-pink', label: 'Deep pink', hex: '#c57c8d' },
    { token: 'blue-striped', label: 'Blå stripet', hex: '#7d9abc' },
    { token: 'capuccino', label: 'Capuccino', hex: '#8a6a52' },
    { token: 'cappuccino', label: 'Cappuccino', hex: '#8a6a52' },
    { token: 'off-white', label: 'Offwhite', hex: '#f6f0e7' },
    { token: 'offwhite', label: 'Hvit', hex: '#f6f0e7' },
    { token: 'espresso', label: 'Espresso', hex: '#33211d' },
    { token: 'brown', label: 'Brun', hex: '#5a3a2b' },
    { token: 'brun', label: 'Brun', hex: '#5a3a2b' },
    { token: 'black', label: 'Svart', hex: '#111111' },
    { token: 'svart', label: 'Svart', hex: '#111111' },
    { token: 'white', label: 'Hvit', hex: '#ffffff' },
    { token: 'hvit', label: 'Hvit', hex: '#ffffff' },
    { token: 'cream', label: 'Krem', hex: '#efe3d5' },
    { token: 'beige', label: 'Beige', hex: '#d8c4aa' },
    { token: 'pink', label: 'Rosa', hex: '#f2cbd2' },
    { token: 'rosa', label: 'Rosa', hex: '#f2cbd2' },
    { token: 'navy', label: 'Navy', hex: '#182236' },
    { token: 'blue', label: 'Blå', hex: '#7d9abc' },
    { token: 'bla', label: 'Blå', hex: '#7d9abc' },
    { token: 'green', label: 'Grønn', hex: '#6f7f64' },
    { token: 'gronn', label: 'Grønn', hex: '#6f7f64' },
    { token: 'red', label: 'Rød', hex: '#9d4141' },
    { token: 'rod', label: 'Rød', hex: '#9d4141' },
    { token: 'grey', label: 'Grå', hex: '#929292' },
    { token: 'gray', label: 'Grå', hex: '#929292' },
    { token: 'gra', label: 'Grå', hex: '#929292' },
  ].sort((a, b) => b.token.length - a.token.length);

  PDP.registerModule('siblings', {
    siblingColorForHandle(handle) {
      const normalized = String(handle || '').toLowerCase().replace(/_/g, '-');
      return SIBLING_COLOR_TOKENS.find((color) => normalized.endsWith(`-${color.token}`) || normalized === color.token) || null;
    },

    siblingKnownColorForText(value) {
      const token = this.siblingTextToken(value);
      if (!token) return null;
      const exactToken = SIBLING_COLOR_TOKENS.find((color) => token === this.siblingTextToken(color.token));
      if (exactToken) return exactToken;
      const exactLabel = SIBLING_COLOR_TOKENS.find((color) => token === this.siblingTextToken(color.label));
      if (exactLabel) return exactLabel;
      return null;
    },

    siblingReadableColorLabel(value) {
      return String(value || '')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    },

    siblingColorBaseTokens() {
      if (!this._siblingColorBaseTokens) {
        this._siblingColorBaseTokens = new Set();
        SIBLING_COLOR_TOKENS.forEach((color) => {
          this._siblingColorBaseTokens.add(this.siblingTextToken(color.token));
          this._siblingColorBaseTokens.add(this.siblingTextToken(color.label));
        });
      }
      return this._siblingColorBaseTokens;
    },

    siblingColorFromValue(value) {
      const known = this.siblingKnownColorForText(value);
      if (known) return known;
      const token = this.siblingTextToken(value);
      if (!token) return null;
      return {
        token,
        label: this.siblingReadableColorLabel(value) || token,
        hex: '#eeeeee',
      };
    },

    siblingColorForTitle(title) {
      const value = String(title || '').trim();
      if (!value) return null;

      const parenthetical = value.match(/\(([^)]+)\)/g) || [];
      for (const part of parenthetical) {
        const color = this.siblingColorFromValue(part.replace(/[()]/g, ''));
        if (color) return color;
      }

      const words = value.split(/\s+/).filter(Boolean);
      for (let size = Math.min(3, words.length); size >= 1; size -= 1) {
        const color = this.siblingKnownColorForText(words.slice(-size).join(' '));
        if (color) return color;
      }

      return null;
    },

    siblingInferredColorFromSuffix(handle, baseHint) {
      const normalized = String(handle || '').toLowerCase().replace(/_/g, '-');
      const base = String(baseHint || '').toLowerCase().replace(/_/g, '-');
      if (!normalized || !base || !normalized.startsWith(`${base}-`)) return null;
      const token = normalized.slice(base.length + 1).replace(/^-+|-+$/g, '');
      if (!token || token.length > 40) return null;
      const exact = this.siblingKnownColorForText(token);
      if (exact) return exact;
      return this.siblingColorFromValue(token);
    },

    siblingColorForProduct(product, baseHint) {
      const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
      return this.siblingColorForTitle(product?.title)
        || this.siblingColorForHandle(handle)
        || this.siblingInferredColorFromSuffix(handle, baseHint)
        || null;
    },

    siblingBaseHandle(handle, colorToken) {
      const normalized = String(handle || '').toLowerCase().replace(/_/g, '-');
      const color = this.siblingColorForHandle(normalized);
      let base = color ? normalized.slice(0, -(`-${color.token}`).length) : normalized;
      const explicitToken = this.siblingTextToken(colorToken);
      if (!color && explicitToken && normalized.endsWith(`-${explicitToken}`)) {
        base = normalized.slice(0, -(`-${explicitToken}`).length);
      }
      return base;
    },

    siblingTextToken(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/å/g, 'a')
        .replace(/ø/g, 'o')
        .replace(/æ/g, 'ae')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },

    stripSiblingColorFromTitle(title) {
      const value = String(title || '').trim();
      const withoutParenthetical = value.replace(/\s*\([^)]*\)\s*/g, ' ').replace(/\s+/g, ' ').trim();
      if (withoutParenthetical && withoutParenthetical !== value) return withoutParenthetical;

      const words = value.split(/\s+/).filter(Boolean);
      if (words.length <= 1) return value;

      if (!this.siblingTitleColorTokens) {
        this.siblingTitleColorTokens = new Set();
        SIBLING_COLOR_TOKENS.forEach((color) => {
          this.siblingTitleColorTokens.add(this.siblingTextToken(color.token));
          this.siblingTitleColorTokens.add(this.siblingTextToken(color.label));
        });
      }

      for (let size = Math.min(3, words.length - 1); size >= 1; size -= 1) {
        const tail = this.siblingTextToken(words.slice(-size).join(' '));
        if (this.siblingTitleColorTokens.has(tail)) return words.slice(0, -size).join(' ');
      }

      return value;
    },

    async siblingLastImage(handle) {
      const normalizedHandle = String(handle || '').trim();
      if (!normalizedHandle) return '';
      if (!this.siblingImageCache) this.siblingImageCache = new Map();
      if (this.siblingImageCache.has(normalizedHandle)) return this.siblingImageCache.get(normalizedHandle);

      let image = '';
      const data = await this.fetchProductJson(normalizedHandle);
      const images = Array.isArray(data?.images) ? data.images : [];
      image = images.length ? images[images.length - 1] : (data?.featured_image || '');
      if (image && String(image).startsWith('//')) image = `${window.location.protocol}${image}`;

      this.siblingImageCache.set(normalizedHandle, image);
      return image;
    },

    async hydrateSiblingImages(items) {
      await Promise.all(items.map(async (item) => {
        if (!item?.handle) return;
        const image = await this.siblingLastImage(item.handle);
        if (image) item.image = image;
      }));
      return items;
    },

    handleFromProductUrl(url) {
      try {
        const parsed = new URL(url, window.location.origin);
        const match = parsed.pathname.match(/\/products\/([^/?#]+)/);
        return match ? match[1] : '';
      } catch (e) {
        return '';
      }
    },

    siblingBaseFromTags(handle, tags) {
      const normalizedHandle = String(handle || '').toLowerCase().replace(/_/g, '-');
      if (!normalizedHandle) return '';
      const genericTags = new Set(['sale', 'salg', 'outlet', 'new', 'nyheter', 'drop', 'genser', 'hoodie', 'set', 'sett', 'bundle']);
      const colorTags = this.siblingColorBaseTokens();
      return String(tags || '')
        .split(/[,\s]+/)
        .map((tag) => this.siblingTextToken(tag))
        .filter((tag) => tag.length > 2 && !genericTags.has(tag) && !colorTags.has(tag) && normalizedHandle.startsWith(`${tag}-`))
        .sort((a, b) => b.length - a.length)[0] || '';
    },

    siblingProductFromSearch(product, baseHint) {
      const handle = product?.handle || this.handleFromProductUrl(product?.url || '');
      if (!handle) return null;
      const color = this.siblingColorForProduct({ ...product, handle }, baseHint)
        || this.siblingColorFromValue(product?.title || handle);
      const knownBase = this.siblingBaseHandle(handle, color?.token);
      const normalizedBaseHint = String(baseHint || '').toLowerCase().replace(/_/g, '-');
      const base = (knownBase === handle && normalizedBaseHint && handle.startsWith(`${normalizedBaseHint}-`))
        ? normalizedBaseHint
        : knownBase;
      return {
        handle,
        base,
        title: product?.title || '',
        url: `/products/${handle}`,
        image: product?.image || product?.featured_image?.url || '',
        color,
      };
    },

    siblingCandidateColors() {
      if (!this._siblingCandidateColors) {
        const seen = new Set();
        this._siblingCandidateColors = SIBLING_COLOR_TOKENS.filter((color) => {
          const key = this.siblingTextToken(color.token);
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }
      return this._siblingCandidateColors;
    },

    async siblingProductsFromHandleCandidates(current) {
      const base = String(current?.base || '').trim();
      const currentHandle = String(current?.handle || '').trim();
      if (!base || !currentHandle || base === currentHandle) return [];

      const candidates = [];
      const seenHandles = new Set([currentHandle]);
      this.siblingCandidateColors().forEach((color) => {
        [color.token, color.token.replace(/-/g, '')].forEach((suffix) => {
          const handle = `${base}-${suffix}`;
          if (!suffix || seenHandles.has(handle)) return;
          seenHandles.add(handle);
          candidates.push({ handle, color });
        });
      });

      const found = [];
      const batchSize = 5;
      for (let i = 0; i < candidates.length; i += batchSize) {
        const batch = candidates.slice(i, i + batchSize);
        const products = await Promise.all(batch.map(async (candidate) => {
          const product = await this.fetchProductJson(candidate.handle);
          if (!product?.handle) return null;
          const handle = product.handle || candidate.handle;
          const color = this.siblingColorForProduct(product, base) || candidate.color;
          return {
            handle,
            base,
            title: product.title || '',
            url: `/products/${handle}`,
            image: (Array.isArray(product.images) && product.images.length ? product.images[product.images.length - 1] : product.featured_image) || '',
            color,
          };
        }));
        products.filter(Boolean).forEach((product) => found.push(product));
      }
      return found;
    },

    renderSiblingColorPicker(root, items) {
      const swatches = root.querySelector('[data-pdp-sibling-swatches]');
      if (!swatches || !items.length) return;
      const currentHandle = root.dataset.currentHandle || this.section.dataset.productHandle || '';
      const byColor = new Map();
      items.forEach((item) => {
        const colorKey = this.siblingTextToken(item?.color?.token || item?.color?.label || '');
        if (!item?.handle || !colorKey) return;
        const existing = byColor.get(colorKey);
        if (!existing || item.handle === currentHandle) byColor.set(colorKey, item);
      });
      const distinctItems = Array.from(byColor.values());
      // Én farge = ingen valgmulighet: raden er server-rendret hidden og
      // vises kun når vi faktisk har 2+ distinkte farger å velge mellom.
      root.hidden = distinctItems.length < 2;
      if (distinctItems.length < 2) return;

      const current = distinctItems.find((item) => item.handle === currentHandle) || distinctItems[0];
      const label = root.querySelector('[data-pdp-sibling-color-label]');
      if (label && current?.color?.label) label.textContent = current.color.label;

      const ordered = distinctItems.slice().sort((a, b) => {
        if (a.handle === currentHandle) return -1;
        if (b.handle === currentHandle) return 1;
        return a.color.label.localeCompare(b.color.label, 'nb');
      });

      swatches.replaceChildren(...ordered.map((item) => {
        const swatch = document.createElement('a');
        swatch.className = 'pdp-clean__sibling-swatch';
        swatch.href = item.url;
        swatch.setAttribute('aria-label', item.color.label);
        swatch.dataset.pdpSiblingSwatch = '';
        swatch.dataset.colorToken = item.color.token;
        if (item.handle === currentHandle) {
          swatch.classList.add('is-active');
          swatch.setAttribute('aria-current', 'page');
          this.on(swatch, 'click', (event) => event.preventDefault());
        }

        const media = document.createElement('span');
        media.className = 'pdp-clean__sibling-swatch-media';
        media.setAttribute('aria-hidden', 'true');
        media.style.setProperty('--pdp-sibling-color', item.color.hex);
        if (item.image) media.style.setProperty('--pdp-sibling-image', `url("${item.image}")`);

        const name = document.createElement('span');
        name.className = 'pdp-clean__visually-hidden';
        name.textContent = item.color.label;

        swatch.append(media, name);
        return swatch;
      }));
    },

    syncServerSiblingVisibility(root) {
      if (!root) return;
      const swatches = Array.from(root.querySelectorAll('[data-pdp-sibling-swatch]'));
      const colorKeys = new Set(swatches
        .map((swatch) => this.siblingTextToken(swatch.dataset.colorToken || swatch.getAttribute('aria-label') || ''))
        .filter(Boolean));
      if (swatches.length < 2 || colorKeys.size < 2) root.hidden = true;
    },

    async bindSiblingColorPicker() {
      const root = this.section.querySelector('[data-pdp-sibling-colors]');
      if (!root || root.dataset.pdpSiblingBound === 'true') return;
      root.dataset.pdpSiblingBound = 'true';

      const currentHandle = root.dataset.currentHandle || this.section.dataset.productHandle || '';
      const currentTitle = root.dataset.currentTitle || '';
      const handleColor = this.siblingColorForHandle(currentHandle);
      const titleColor = this.siblingColorForTitle(currentTitle);
      const datasetColor = {
        token: root.dataset.currentColorToken || '',
        label: root.dataset.currentColorLabel || '',
        hex: root.dataset.currentColorHex || '#eeeeee',
      };
      const currentColor = (datasetColor.token ? datasetColor : null)
        || titleColor
        || handleColor
        || this.siblingColorFromValue(currentTitle || currentHandle);
      const handleBase = this.siblingBaseHandle(currentHandle, handleColor?.token || currentColor?.token);
      const tagBase = this.siblingBaseFromTags(currentHandle, root.dataset.currentTags);
      const currentBase = handleBase && handleBase !== currentHandle ? handleBase : (tagBase || handleBase);
      const activeSwatch = root.querySelector('[data-pdp-sibling-swatch].is-active');
      if (activeSwatch) this.on(activeSwatch, 'click', (event) => event.preventDefault());
      this.syncServerSiblingVisibility(root);
      if (root.dataset.pdpSiblingSource === 'metafield') {
        // custom.sibling_products er fasiten (synket fra theme.siblings via
        // tools/sync-sibling-metafields.mjs) — aldri klient-gjetting oppå
        // kuraterte data. Server-raden står som den er (også når skjult =
        // produktet HAR ingen fargesøsken).
        return;
      }
      if (root.dataset.pdpSiblingSource === 'collection' && root.querySelectorAll('[data-pdp-sibling-swatch]').length > 1 && !root.hidden) {
        return;
      }
      if (!currentHandle) return;

      const current = {
        handle: currentHandle,
        base: currentBase,
        title: currentTitle,
        url: root.dataset.currentUrl || this.section.dataset.productUrl || window.location.pathname,
        image: root.dataset.currentColorImage || '',
        color: currentColor,
      };

      try {
        const strippedTitle = this.stripSiblingColorFromTitle(current.title);
        const queries = [strippedTitle, current.base.replace(/-/g, ' '), current.title]
          .map((query) => String(query || '').trim())
          .filter(Boolean);
        const products = [];
        const seenQueries = new Set();

        for (const query of queries) {
          const normalizedQuery = query.toLowerCase();
          if (seenQueries.has(normalizedQuery)) continue;
          seenQueries.add(normalizedQuery);

          const url = `${this.cartUrl('search/suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) continue;
          const data = await response.json();
          const resultProducts = data?.resources?.results?.products || [];
          products.push(...resultProducts);
          const hasSibling = resultProducts
            .map((product) => this.siblingProductFromSearch(product, current.base))
            .some((item) => item && item.base === current.base && item.handle !== current.handle);
          if (hasSibling) break;
        }

        const siblings = products
          .map((product) => this.siblingProductFromSearch(product, current.base))
          .filter((item) => item && item.base === current.base);
        if (!siblings.some((item) => item.handle !== current.handle)) {
          siblings.push(...(await this.siblingProductsFromHandleCandidates(current)));
        }
        const byHandle = new Map();
        [current, ...siblings].forEach((item) => {
          if (!item?.handle || item.base !== current.base) return;
          if (byHandle.has(item.handle)) return;
          byHandle.set(item.handle, { ...item, url: item.url || `/products/${item.handle}` });
        });
        const items = Array.from(byHandle.values());
        if (items.length > 1) {
          await this.hydrateSiblingImages(items);
          this.renderSiblingColorPicker(root, items);
        }
      } catch (e) {
        // Singleton swatch from Liquid is still usable if predictive search is unavailable.
      }
    },
  });
})();
